<template>
    <div>
        <PageHeader>
            {{ $t('catalog') }} - {{ $t('products') }}
            <template #side>
                <ButtonIcon
                    class="ml-2"
                    icon="plus"
                    variant="primary"
                    :disabled="isLoading"
                    @click="editItem()"
                >
                    {{ $t('addNew') }}
                </ButtonIcon>
            </template>
        </PageHeader>

        <Card :header="$t('filters')">
            <b-form-group :label="$t('id')" label-cols-sm="3">
                <b-form-input v-model="filters.id" :disabled="isLoading" />
            </b-form-group>

            <b-form-group :label="$t('name')" label-cols-sm="3">
                <b-form-input v-model="filters.name" :disabled="isLoading" />
            </b-form-group>

            <b-form-group :label="$t('author')" label-cols-sm="3">
                <b-form-input v-model="filters.author" :disabled="isLoading" />
            </b-form-group>

            <b-form-group :label="$t('brand')" label-cols-sm="3">
                <Multiselect
                    v-model="filters.brand"
                    :fetch-options-function="fetchBrands"
                    :parse-options-function="data => data.items || []"
                    :asynchronous="false"
                    track-by="id"
                    label="name"
                    :disabled="isLoading"
                />
            </b-form-group>

            <b-form-group :label="$t('description')" label-cols-sm="3">
                <b-form-input v-model="filters.description" :disabled="isLoading" />
            </b-form-group>

            <b-form-group :label="$t('source')" label-cols-sm="3">
                <b-form-input v-model="filters.source" :disabled="isLoading" />
            </b-form-group>

            <b-form-group :label="$t('category')" label-cols-sm="3">
                <Multiselect
                    v-model="filters.categories"
                    :options="categoriesOptionsFormat"
                    :placeholder="$t('selectField.placeholder')"
                    :show-checkboxes="true"
                    :multiple="true"
                    :clear-on-select="false"
                    :close-on-select="false"
                    :show-selected="true"
                    :loading="isCategoriesLoading"
                    :disabled="isLoading"
                />
            </b-form-group>

            <ButtonIcon :loading="isLoading" icon="filter" @click="fetchData()">
                {{ $t('filter') }}
            </ButtonIcon>
        </Card>

        <DataTable
            :items="items"
            :fields="fields"
            :total-rows="products.max"
            :current-page.sync="currentPage"
            :per-page.sync="perPage"
            :sort-by.sync="sortBy"
            :sort-direction.sync="sortDirection"
            :loading="isLoading"
            no-local-sorting
        >
            <template #cell(id)="data">
                <b-link :to="{ name: 'CatalogProductsDetails', params: { id: data.item.id } }">{{ data.item.id }}</b-link>
            </template>
            <template #cell(name)="data">
                <b-link :to="{ name: 'CatalogProductsDetails', params: { id: data.item.id } }">{{ data.item.name }}</b-link>
            </template>
            <template #cell(categories)="data">
                <ul class="m-0 p-0">
                    <li v-for="category in data.value" :key="category.id">
                        {{ category.name }}
                    </li>
                </ul>
            </template>
            <template #cell(action)="data">
                <div class="d-flex gap-1 justify-content-end">
                    <ButtonIcon
                        size="sm"
                        icon="eye"
                        variant="info"
                        :disabled="isItemBusy(data.item.id)"
                        :loading="isItemUpdated(data.item.id)"
                        @click="$router.push({ name: 'CatalogProductsDetails', params: { id: data.item.id } })"
                    />
                    <ButtonIcon
                        size="sm"
                        icon="trash-2"
                        variant="danger"
                        :disabled="isItemBusy(data.item.id)"
                        :loading="isItemDeleted(data.item.id)"
                        @click="deleteItem(data.item.id)"
                    />
                    <ButtonIcon
                        v-b-tooltip.hover.noninteractive
                        :title="$t('connectDuplicates')"
                        size="sm"
                        icon="copy"
                        variant="info"
                        :disabled="isItemBusy(data.item.id)"
                        :loading="isItemDeleted(data.item.id)"
                        @click="duplicateItem(data.item)"
                    />
                </div>
            </template>
        </DataTable>
        <ModalForm />
        <ModalDuplicates />
    </div>
</template>



<script>
    import { createItemKey } from '@/store/modules/moduleFactory';
    import categoryService from '@/services/categoryService';
    import DataTable from '@/components/common/DataTable.vue';
    import Multiselect from '@/components/common/Multiselect.vue';
    import ModalForm from './parts/ModalForm.vue';
    import ModalDuplicates from './parts/ModalDuplicates.vue';

    export default {
        name: 'CatalogProducts',
        components: {
            DataTable,
            Multiselect,
            ModalForm,
            ModalDuplicates,
        },
        provide() {
            return {
                parentComponent: this,
            };
        },
        data() {
            return {
                perPage: 10,
                currentPage: 1,
                sortBy: 'id',
                sortDirection: 'desc',
                categories: [],
                isCategoriesLoading: false,
                filters: {
                    id: null,
                    name: '',
                    author: '',
                    brand: null,
                    description: '',
                    source: '',
                    categories: [],
                },
            };
        },
        computed: {
            fields() {
                return [
                    {
                        key: 'id',
                        label: this.$t('id'),
                        sortable: true,
                    },
                    {
                        key: 'name',
                        label: this.$t('name'),
                        sortable: true,
                    },
                    {
                        key: 'author',
                        label: this.$t('author'),
                    },
                    {
                        key: 'brand.name',
                        label: this.$t('brand'),
                    },
                    {
                        key: 'description',
                        label: this.$t('description'),
                    },
                    {
                        key: 'source',
                        label: this.$t('source'),
                    },
                    {
                        key: 'categories',
                        label: this.$t('categories'),
                    },
                    {
                        key: 'action',
                        label: '',
                        tdClass: 'text-right',
                        thClass: 'text-right',
                    },
                ];
            },
            requestParams() {
                return {
                    ...this.tableParams,
                    ...this.filters,
                    categories: this.filters.categories.map(item => item.value).join(','),
                    brand: this.filters.brand?.id || null,
                };
            },
            tableParams() {
                return {
                    page: this.currentPage,
                    perPage: this.perPage,
                    sort: this.sortBy,
                    order: this.sortDirection.toUpperCase(),
                };
            },
            products() {
                return this.$store.getters['catalog/getProducts'];
            },
            items() {
                return this.products.items || [];
            },
            error() {
                return this.$store.getters['catalog/getError']('products');
            },
            isLoading() {
                return this.$store.getters['catalog/getLoading']('products');
            },
            isItemBusy() {
                return id => this.$store.getters['catalog/getBusyItem']('products', createItemKey({ id }));
            },
            isItemUpdated() {
                return id => this.$store.getters['catalog/getUpdatedItem']('products', createItemKey({ id }));
            },
            isItemDeleted() {
                return id => this.$store.getters['catalog/getDeletedItem']('products', createItemKey({ id }));
            },
            categoriesOptionsFormat() {
                return this.categories.map(({ id, name }) => ({ value: id, text: name }));
            },
        },
        watch: {
            tableParams: {
                immediate: true,
                handler: 'fetchData',
            },
            error() {
                if (this.error) this.$toastr.error(this.error.message, this.$t('errorOccurred'));
            },
        },
        methods: {
            clearData() {
                this.$store.commit('catalog/clearProducts');
            },
            fetchData() {
                this.$store.dispatch('catalog/fetchProducts', { requestParams: this.requestParams }).catch(() => {});
            },
            deleteItem(id) {
                this.$bvModal
                    .msgBoxConfirm(this.$t('confirmYouWantDelete'), {
                        title: this.$t('confirm'),
                        okTitle: this.$t('yes'),
                        cancelTitle: this.$t('cancel'),
                        okVariant: 'danger',
                    })
                    .then(value => {
                        if (value) {
                            this.$store
                                .dispatch('catalog/deleteProducts', { urlParams: { id } })
                                .then(this.fetchData)
                                .catch(() => {});
                        }
                    });
            },
            editItem(item = null) {
                this.$root.$emit('bv::show::modal', 'modal-form-catalog-products');
                this.$root.$emit('modal-form-catalog-products', item);
            },
            async fetchFilterCategories() {
                this.isCategoriesLoading = true;
                this.categories = await categoryService.getCategories();
                this.isCategoriesLoading = false;
            },
            fetchBrands() {
                const requestParams = {
                    sort: 'name',
                    order: 'ASC',
                    page: 1,
                    perPage: Number.MAX_SAFE_INTEGER,
                };
                return this.$store.dispatch('catalog/fetchBrands', { requestParams }).catch(() => {});
            },
            duplicateItem(item) {
                this.$root.$emit('bv::show::modal', 'modal-form-catalog-products-duplicates');
                this.$root.$emit('modal-form-catalog-products-duplicates', item);
            },
        },
        created() {
            this.clearData();
            this.fetchFilterCategories();
        },
    };
</script>
